import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import '../css/Admin_css/admin-customize.css';
import COLORS from '../resources/colors/COLORS';
import myToast from '../Utils/MyToast';

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade7 || '#01579B'} 0%, ${COLORS.primary_color_shade8 || '#0277BD'} 100%)`;
const DATE_BOX_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade0 || '#E1F5FE'} 0%, ${COLORS.white} 100%)`;

function formatDateParts(date) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return {
    display: `${dd}-${mm}-${yyyy}`,
    api: `${yyyy}-${mm}-${dd}`,
  };
}

function parseYyyyMmDd(value) {
  const [yyyy, mm, dd] = String(value || '').split('-').map(Number);
  return new Date(yyyy, mm - 1, dd);
}

function getDayOffsetFromToday(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function getDateDisplayMeta(apiDate) {
  if (!apiDate) return null;
  const date = parseYyyyMmDd(apiDate);
  const offset = getDayOffsetFromToday(date);
  return {
    weekday: date.toLocaleDateString('en-IN', { weekday: 'long' }),
    day: String(date.getDate()).padStart(2, '0'),
    month: date.toLocaleDateString('en-IN', { month: 'short' }),
    year: date.getFullYear(),
    display: formatDateParts(date).display,
    offset,
    offsetLabel:
      offset === 0 ? 'Today' : offset === -1 ? 'Yesterday' : offset === 1 ? 'Tomorrow' : null,
  };
}

function CollectionDatePicker({ value, onChange, onPrevious, onNext }) {
  const dateInputRef = useRef(null);
  const meta = getDateDisplayMeta(value);

  const openCalendar = () => {
    const input = dateInputRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.focus();
      input.click();
    }
  };

  return (
    <div className="fee-date-picker-wrap">
      <div className="fee-date-picker-row">
        <button
          type="button"
          className="fee-date-nav-btn"
          onClick={onPrevious}
          aria-label="Previous day"
        >
          <i className="fa fa-chevron-left" aria-hidden="true" />
        </button>

        <div className="fee-date-picker-box" onClick={openCalendar} role="presentation">
          <div className="fee-date-picker-icon">
            <i className="fa fa-calendar" aria-hidden="true" />
          </div>
          <div className="fee-date-picker-text">
            {meta ? (
              <>
                <span className="fee-date-weekday">{meta.weekday}</span>
                <span className="fee-date-main">
                  {meta.day} {meta.month} {meta.year}
                </span>
              </>
            ) : (
              <span className="fee-date-main">Select date</span>
            )}
          </div>
          {meta?.offsetLabel && (
            <span className="fee-date-offset-badge">{meta.offsetLabel}</span>
          )}
          <input
            ref={dateInputRef}
            type="date"
            className="fee-date-input-overlay"
            value={value}
            onChange={onChange}
            aria-label="Collection date"
            title="Select collection date"
          />
          <span className="fee-date-open-hint">
            <i className="fa fa-caret-down" aria-hidden="true" />
          </span>
        </div>

        <button
          type="button"
          className="fee-date-nav-btn"
          onClick={onNext}
          aria-label="Next day"
        >
          <i className="fa fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
      {meta && (
        <div className="fee-date-footer">
          <span className="fee-date-footer-label">Collection date</span>
          <span className="fee-date-footer-value">{meta.display}</span>
        </div>
      )}
    </div>
  );
}

function formatINR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function SectionCard({ icon, title, children, style }) {
  return (
    <div
      style={{
        background: COLORS.white,
        border: `1px solid ${COLORS.primary_color_shade2 || '#81D4FA'}`,
        borderRadius: '14px',
        padding: '14px 16px',
        marginBottom: '12px',
        boxShadow: '0 2px 10px rgba(2, 136, 209, 0.08)',
        ...style,
      }}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: 700,
          color: COLORS.black8 || '#333333',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        <span
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: COLORS.primary_color_shade0 || '#E1F5FE',
            color: COLORS.primary_color_shade7 || '#0288D1',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
          }}
        >
          <i className={`fa ${icon}`} aria-hidden="true" />
        </span>
        {title}
      </div>
      {children}
    </div>
  );
}

const ModalFeeConfirmation = ({
  netPayableAmount,
  action,
  action2,
}) => {
  const [show, setShow] = useState(true);
  const [TRANSACTION_DATE, setTRANSACTION_DATE] = useState('');
  const [TRANSACTION_DATE2, setTRANSACTION_DATE2] = useState('');
  const [day_count, setDay_count] = useState(0);
  const [PaymentRemark, setPaymentRemark] = useState('');
  const [PAYMENT_MODE, setPAYMENT_MODE] = useState('CASH');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requestIdRef = useRef(`fee-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);

  useEffect(() => {
    applyCollectionDate(new Date());
  }, []);

  const applyCollectionDate = (date) => {
    const { display, api } = formatDateParts(date);
    setTRANSACTION_DATE(display);
    setTRANSACTION_DATE2(api);
    setDay_count(getDayOffsetFromToday(date));
  };

  const nextDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + day_count + 1);
    applyCollectionDate(date);
  };

  const previousDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + day_count - 1);
    applyCollectionDate(date);
  };

  const handleCollectionDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    applyCollectionDate(parseYyyyMmDd(value));
  };

  const regChange = (e) => {
    if (e.target.id === 'PaymentRemark') {
      setPaymentRemark(e.target.value);
    }
  };

  const HandleClose = () => {
    setShow(false);
    if (action) {
      action();
    }
  };

  const submitFeeDetails = () => {
    if (isSubmitting) {
      return;
    }
    if (!TRANSACTION_DATE2) {
      myToast.notify_warning('Please select a collection date');
      return;
    }
    setIsSubmitting(true);
    setShow(false);
    if (action2) {
      //Created By Cursor — send trimmed remark only; blank stays empty (no garbage)
      const cleanRemark = String(PaymentRemark || '').trim();
      action2(TRANSACTION_DATE2, PAYMENT_MODE, cleanRemark, requestIdRef.current);
    }
  };

  const paymentModeStyle = (mode) => ({
    flex: '1 1 0',
    minWidth: '90px',
    padding: '7px 8px',
    borderRadius: '8px',
    border: `1.5px solid ${PAYMENT_MODE === mode ? COLORS.primary_color_shade6 : COLORS.grey3}`,
    background:
      PAYMENT_MODE === mode
        ? COLORS.primary_color_shade0 || '#E1F5FE'
        : COLORS.white,
    color: PAYMENT_MODE === mode ? COLORS.primary_color_shade9 : COLORS.black7,
    fontSize: '12px',
    fontWeight: PAYMENT_MODE === mode ? 700 : 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    lineHeight: 1.2,
    textAlign: 'center',
  });

  return (
    <Modal
      show={show}
      onHide={HandleClose}
      animation={false}
      centered
      size="lg"
      backdrop="static"
      dialogClassName="modal-fee-confirmation"
      backdropClassName="modal-fee-confirmation-backdrop"
      contentClassName="modal-fee-confirmation-content"
    >
      <div
        style={{
          background: COLORS.white,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.28)',
        }}
      >
        <Modal.Header
          closeButton
          style={{
            background: HEADER_GRADIENT,
            color: COLORS.white,
            border: 'none',
            padding: '20px 24px',
            minHeight: '80px',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-30px',
              right: '-10px',
              width: '120px',
              height: '120px',
              background: 'rgba(255, 255, 255, 0.12)',
              borderRadius: '50%',
              filter: 'blur(20px)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-40px',
              left: '20%',
              width: '80px',
              height: '80px',
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '50%',
            }}
          />
          <Modal.Title
            id="contained-modal-title-vcenter"
            style={{
              fontWeight: 800,
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              lineHeight: 1.2,
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                background: 'rgba(255, 255, 255, 0.22)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              }}
            >
              <i className="fa fa-check-circle" style={{ fontSize: '22px' }} />
            </div>
            <div>
              <div>Fee Collection Confirmation</div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  opacity: 0.92,
                  marginTop: '4px',
                }}
              >
                Review amount, date, and payment details before submitting
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            padding: '20px 22px',
            background: `linear-gradient(to bottom, ${COLORS.grey0 || '#FAFAFA'} 0%, ${COLORS.white} 100%)`,
            border: 'none',
          }}
        >
          <div
            style={{
              background: HEADER_GRADIENT,
              borderRadius: '12px',
              padding: '10px 16px',
              marginBottom: '12px',
              boxShadow: '0 4px 14px rgba(2, 136, 209, 0.2)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                color: 'rgba(255, 255, 255, 0.92)',
                marginBottom: '2px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                lineHeight: 1.2,
              }}
            >
              Transaction Amount
            </div>
            <div
              style={{
                fontSize: '24px',
                fontWeight: 800,
                color: COLORS.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                lineHeight: 1.1,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
              }}
            >
              <i className="fa fa-rupee-sign" style={{ fontSize: '18px' }} />
              {formatINR(netPayableAmount)}
            </div>
          </div>

          <SectionCard icon="fa-calendar" title="Collection Date">
            <CollectionDatePicker
              value={TRANSACTION_DATE2}
              onChange={handleCollectionDateChange}
              onPrevious={previousDate}
              onNext={nextDate}
            />
          </SectionCard>

          <SectionCard icon="fa-credit-card" title="Payment Mode" style={{ padding: '10px 12px' }}>
            <div
              role="radiogroup"
              aria-label="Payment mode"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '6px',
              }}
            >
              {[
                { value: 'CASH', label: 'Cash' },
                { value: 'CHEQUE', label: 'Cheque' },
                { value: 'ONLINE', label: 'Online' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={PAYMENT_MODE === value}
                  onClick={() => setPAYMENT_MODE(value)}
                  style={paymentModeStyle(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon="fa-comment" title="Payment Remark" style={{ marginBottom: 0 }}>
            <input
              type="text"
              id="PaymentRemark"
              placeholder="Enter payment remark or description"
              value={PaymentRemark}
              onChange={regChange}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '14px',
                border: `2px solid ${COLORS.grey3 || '#E0E0E0'}`,
                borderRadius: '12px',
                outline: 'none',
                boxSizing: 'border-box',
                background: COLORS.white,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = COLORS.primary_color_shade6 || '#039BE5';
                e.currentTarget.style.boxShadow = `0 0 0 3px ${COLORS.primary_color_shade1 || '#B3E5FC'}40`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = COLORS.grey3 || '#E0E0E0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </SectionCard>
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1px solid ${COLORS.grey2 || '#EEEEEE'}`,
            padding: '16px 22px',
            background: COLORS.white,
            border: 'none',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <Button
            variant="secondary"
            onClick={HandleClose}
            style={{
              padding: '10px 20px',
              borderRadius: '12px',
              border: `2px solid ${COLORS.grey3 || '#E0E0E0'}`,
              background: COLORS.white,
              color: COLORS.black8 || '#333333',
              fontWeight: 700,
              fontSize: '14px',
              minWidth: '100px',
            }}
          >
            Cancel
          </Button>
          <button
            type="button"
            onClick={submitFeeDetails}
            disabled={isSubmitting}
            style={{
              padding: '10px 28px',
              borderRadius: '12px',
              background: HEADER_GRADIENT,
              border: 'none',
              color: COLORS.white,
              fontWeight: 800,
              fontSize: '15px',
              boxShadow: '0 4px 14px rgba(2, 119, 189, 0.35)',
              minWidth: '140px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            <i className="fa fa-check" style={{ marginRight: '8px' }} />
            Submit
          </button>
        </Modal.Footer>
      </div>

      <style>{`
        .modal-fee-confirmation-backdrop {
          background-color: rgba(0, 0, 0, 0.55);
          backdrop-filter: blur(4px);
        }
        .modal-fee-confirmation-content {
          border-radius: 20px;
          overflow: hidden;
          border: none;
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
        }
        .modal-fee-confirmation .modal-content {
          border-radius: 20px;
          overflow: hidden;
          border: none;
        }
        .modal-fee-confirmation .modal-header {
          border-radius: 20px 20px 0 0;
          min-height: 80px;
        }
        .modal-fee-confirmation .modal-footer {
          border-radius: 0 0 20px 20px;
        }
        .modal-fee-confirmation .modal-header .btn-close {
          filter: brightness(0) invert(1);
          opacity: 0.92;
          z-index: 2;
          width: 1.1rem;
          height: 1.1rem;
        }
        .modal-fee-confirmation .modal-header .btn-close:hover {
          opacity: 1;
        }
        .fee-date-picker-wrap {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .fee-date-picker-row {
          display: flex;
          align-items: stretch;
          gap: 8px;
        }
        .fee-date-nav-btn {
          width: 38px;
          min-width: 38px;
          border-radius: 12px;
          border: 2px solid ${COLORS.primary_color_shade2 || '#81D4FA'};
          background: ${COLORS.white};
          color: ${COLORS.primary_color_shade8 || '#0277BD'};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
          padding: 0;
          font-size: 13px;
          box-shadow: 0 1px 4px rgba(2, 136, 209, 0.1);
        }
        .fee-date-nav-btn:hover {
          background: ${COLORS.primary_color_shade0 || '#E1F5FE'};
          border-color: ${COLORS.primary_color_shade5 || '#29B6F6'};
          transform: translateY(-1px);
        }
        .fee-date-picker-box {
          position: relative;
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 14px;
          border: 2px solid ${COLORS.primary_color_shade2 || '#81D4FA'};
          background: ${DATE_BOX_GRADIENT};
          cursor: pointer;
          transition: all 0.15s ease;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 8px rgba(2, 136, 209, 0.1);
        }
        .fee-date-picker-box:hover {
          border-color: ${COLORS.primary_color_shade5 || '#29B6F6'};
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 4px 14px rgba(2, 136, 209, 0.16);
        }
        .fee-date-picker-icon {
          width: 40px;
          height: 40px;
          border-radius: 11px;
          background: ${HEADER_GRADIENT};
          color: ${COLORS.white};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
          box-shadow: 0 3px 10px rgba(2, 119, 189, 0.25);
        }
        .fee-date-picker-text {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .fee-date-weekday {
          font-size: 11px;
          font-weight: 600;
          color: ${COLORS.black6 || '#666'};
          text-transform: uppercase;
          letter-spacing: 0.06em;
          line-height: 1.2;
        }
        .fee-date-main {
          font-size: 16px;
          font-weight: 800;
          color: ${COLORS.primary_color_shade9 || '#01579B'};
          line-height: 1.15;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .fee-date-offset-badge {
          font-size: 10px;
          font-weight: 700;
          padding: 4px 8px;
          border-radius: 999px;
          background: ${COLORS.green_light || '#E8F5E9'};
          color: ${COLORS.leaf_green || '#2E7D32'};
          border: 1px solid ${COLORS.green_dark || '#43A047'};
          white-space: nowrap;
          flex-shrink: 0;
        }
        .fee-date-open-hint {
          color: ${COLORS.primary_color_shade6 || '#039BE5'};
          font-size: 14px;
          flex-shrink: 0;
          opacity: 0.85;
        }
        .fee-date-input-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          border: none;
          background: transparent;
        }
        .fee-date-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 8px;
          background: ${COLORS.primary_color_shade0 || '#E1F5FE'};
          border: 1px dashed ${COLORS.primary_color_shade2 || '#81D4FA'};
        }
        .fee-date-footer-label {
          font-size: 11px;
          font-weight: 600;
          color: ${COLORS.black6 || '#666'};
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .fee-date-footer-value {
          font-size: 12px;
          font-weight: 800;
          color: ${COLORS.primary_color_shade8 || '#0277BD'};
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </Modal>
  );
};

export default ModalFeeConfirmation;
